/** 通用工具函数 */

/** 拼接 className（条件样式） */
export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** Date → 'YYYY-MM-DD'（本地时区） */
export function dateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 'YYYY-MM-DD' → 本地 Date（当天 0 点） */
export function parseDateStr(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function todayStr(): string {
  return dateStr(new Date());
}

/** 两个 'YYYY-MM-DD' 之间的天数差（b - a） */
export function diffDays(a: string, b: string): number {
  const ms = parseDateStr(b).getTime() - parseDateStr(a).getTime();
  return Math.round(ms / 86_400_000);
}

/** 相对日期描述：今天/明天/逾期 N 天/N 天后 */
export function relativeDay(date: string | null): string {
  if (!date) return '';
  const d = diffDays(todayStr(), date);
  if (d === 0) return '今天';
  if (d === 1) return '明天';
  if (d === 2) return '后天';
  if (d < 0) return `逾期 ${-d} 天`;
  return `${d} 天后`;
}

/** ISO 日期时间 → 'YYYY-MM-DD HH:mm' */
export function dateTimeStr(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${dateStr(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 分 → 'X 小时 Y 分钟' */
export function formatMinutes(min: number): string {
  if (min < 60) return `${min} 分钟`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} 小时 ${m} 分钟` : `${h} 小时`;
}

/** 分 → 'HH:MM:SS' 倒计时格式 */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

/** 分 → 'Xm' 紧凑格式 */
export function compactMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h${m}m` : `${h}h`;
}

/** 分 → '¥X.XX' */
export function formatCents(cents: number | null): string {
  if (cents == null) return '';
  return `¥${(cents / 100).toFixed(2)}`;
}

/** 400 message 数组/字符串 → 单行文本 */
export function errorMessage(message: string | string[]): string {
  return Array.isArray(message) ? message.join('；') : message;
}

/** 任务优先级标签 */
export const PRIORITY_LABELS: Record<number, string> = {
  1: '紧急重要',
  2: '重要',
  3: '紧急',
  4: '普通',
};

export const PRIORITY_STYLES: Record<number, string> = {
  1: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  2: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  3: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  4: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

export const STATUS_LABELS: Record<string, string> = {
  TODO: '待办',
  DOING: '进行中',
  DONE: '已完成',
  CANCELLED: '已取消',
  ACTIVE: '进行中',
  ARCHIVED: '已归档',
  OPEN: '待办',
  BROKEN: '未兑现',
};

export const INTERACTION_LABELS: Record<string, string> = {
  MEETING: '见面',
  CALL: '通话',
  MESSAGE: '消息',
  EMAIL: '邮件',
  GIFT: '送礼',
  OTHER: '其他',
};

export const VAULT_TYPE_LABELS: Record<string, string> = {
  LOGIN: '登录帐号',
  CARD: '银行卡',
  NOTE: '安全笔记',
  IDENTITY: '证件',
  WIFI: 'Wi-Fi',
};

/** 优先级选择器选项 */
export const PRIORITY_OPTIONS = [1, 2, 3, 4] as const;

/** localStorage key 统一管理 */
export const STORAGE_KEYS = {
  theme: 'lc-theme',
  vaultKdf: 'lc-vault-kdf',
} as const;
